import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  CustomerCreditLine,
  CustomerItemBought,
  CustomerReturnLine,
} from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { agingText } from '@/features/portal/aging';
import { PurchaseDays } from '@/features/portal/PurchaseDays';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchCustomer, portalKeys } from '@/features/portal/api';
import { formatCount, formatMoney } from '@/lib/format';

type Tab = 'purchases' | 'items' | 'credit' | 'returns';

const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'purchases', label: 'Purchases' },
  { id: 'items', label: 'Items bought' },
  { id: 'credit', label: 'Credit & payments' },
  { id: 'returns', label: 'Returns' },
];

/**
 * One customer: lifetime totals, then everything they have done.
 *
 * Lifetime rather than range-bound on purpose — the question this page
 * answers is "who is this person", and a date filter would answer a different
 * one while looking like the same page.
 */
export function CustomerDetailPage() {
  const { key = '' } = useParams<{ key: string }>();
  const { currency } = usePortal();
  const [tab, setTab] = useState<Tab>('purchases');

  const query = useQuery({
    queryKey: portalKeys.customer(key),
    queryFn: ({ signal }) => fetchCustomer(key, signal),
    enabled: key !== '',
  });

  const money = (v: string) => formatMoney(v, query.data?.currency || currency);

  const itemColumns = useMemo<Column<CustomerItemBought>[]>(
    () => [
      { key: 'name', header: 'Item', pinned: true, cell: (row) => row.name },
      {
        key: 'quantity',
        header: 'Qty',
        align: 'right',
        cell: (row) => formatCount(row.quantity),
      },
      {
        key: 'times',
        header: 'Times',
        align: 'right',
        cell: (row) => formatCount(row.times),
      },
      {
        key: 'spent',
        header: 'Spent',
        align: 'right',
        cell: (row) => money(row.spent),
      },
      {
        key: 'avgPrice',
        header: 'Avg price',
        align: 'right',
        // What they actually paid, averaged — not today's shelf price.
        cell: (row) => money(row.avgPrice),
      },
      { key: 'lastBought', header: 'Last bought', cell: (row) => row.lastBought },
    ],
    [currency, query.data?.currency],
  );

  const creditColumns = useMemo<Column<CustomerCreditLine>[]>(
    () => [
      { key: 'date', header: 'Date', pinned: true, cell: (row) => row.date },
      { key: 'type', header: 'Type', cell: (row) => row.type },
      { key: 'reference', header: 'Ref', cell: (row) => row.reference || '—' },
      {
        key: 'amount',
        header: 'Amount',
        align: 'right',
        cell: (row) => money(row.amount),
      },
      {
        key: 'balance',
        header: 'Balance',
        align: 'right',
        // Runs downwards; the last one is what they owe now.
        cell: (row) => <span className="font-medium">{money(row.balance)}</span>,
      },
    ],
    [currency, query.data?.currency],
  );

  const returnColumns = useMemo<Column<CustomerReturnLine>[]>(
    () => [
      {
        key: 'returnNumber',
        header: 'Return',
        pinned: true,
        cell: (row) => (
          <div>
            <span className="font-medium text-slate-900">{row.returnNumber}</span>
            <p className="mt-0.5 whitespace-nowrap text-xs text-slate-400">{row.date}</p>
          </div>
        ),
      },
      { key: 'orderNumber', header: 'Against bill', cell: (row) => row.orderNumber },
      { key: 'reason', header: 'Reason', cell: (row) => row.reason || '—' },
      {
        key: 'refund',
        header: 'Refund',
        align: 'right',
        cell: (row) => money(row.refund),
      },
      { key: 'status', header: 'Status', cell: (row) => row.status },
    ],
    [currency, query.data?.currency],
  );

  return (
    <>
      <PageHeader
        title={query.data?.name || 'Customer'}
        description={
          query.data
            ? [query.data.phone, query.data.branches.join(', ')]
                .filter((part) => part !== '')
                .join(' · ')
            : 'Everything this customer has bought, owed and returned.'
        }
        actions={
          <Link to="/portal/customers" className="btn-ghost">
            Back to customers
          </Link>
        }
      />

      <div className="space-y-6">
        <AsyncSection
          state={query}
          skeleton={
            <KpiGrid>
              {Array.from({ length: 6 }).map((_, i) => (
                <KpiTileSkeleton key={i} />
              ))}
            </KpiGrid>
          }
        >
          {(data) => (
            <KpiGrid>
              <KpiTile label="Lifetime spend" value={money(data.spent)} />
              <KpiTile label="Visits" value={formatCount(data.visits)} hint="One per bill" />
              <KpiTile label="Avg bill" value={money(data.avgBill)} />
              <KpiTile label="Owes now" value={money(data.outstanding)} />
              <KpiTile label="Points" value={formatCount(data.points)} />
              <KpiTile
                label="Last visit"
                value={data.lastVisit ?? '—'}
                hint={data.agingDays === null ? undefined : agingText(data.agingDays)}
              />
            </KpiGrid>
          )}
        </AsyncSection>

        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              aria-pressed={tab === t.id}
              onClick={() => setTab(t.id)}
              className={
                tab === t.id
                  ? 'rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white'
                  : 'rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900'
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        <SectionCard
          headingId="customer-tab"
          title={TABS.find((t) => t.id === tab)?.label ?? ''}
          description={
            tab === 'items'
              ? 'Every product this customer has ever bought, across all their bills.'
              : tab === 'purchases'
                ? 'Their bills by date, newest first, with how long ago each day was.'
                : undefined
          }
        >
          <AsyncSection
            state={query}
            skeleton={<TableSkeleton rows={6} columns={6} />}
            isEmpty={(data) =>
              tab === 'purchases'
                ? data.purchases.length === 0
                : tab === 'items'
                  ? data.itemsBought.length === 0
                  : tab === 'credit'
                    ? data.credit.length === 0
                    : data.returns.length === 0
            }
            emptyTitle="Nothing here yet"
            emptyDescription="Only sales billed to this customer appear. Walk-in sales belong to nobody."
          >
            {(data) =>
              tab === 'purchases' ? (
                // Grouped by the day they were placed, newest first, with how
                // long ago each day was.
                <PurchaseDays
                  days={data.purchaseDays}
                  currency={data.currency || currency}
                />
              ) : tab === 'items' ? (
                <DataTable
                  caption="Products this customer has bought"
                  columns={itemColumns}
                  rows={data.itemsBought}
                  rowKey={(r) => r.name}
                />
              ) : tab === 'credit' ? (
                <DataTable
                  caption="Credit given and payments received"
                  columns={creditColumns}
                  rows={data.credit}
                  rowKey={(r) => `${r.date}-${r.reference}-${r.amount}`}
                />
              ) : (
                <DataTable
                  caption="Returns against this customer's bills"
                  columns={returnColumns}
                  rows={data.returns}
                  rowKey={(r) => r.returnNumber}
                />
              )
            }
          </AsyncSection>
        </SectionCard>
      </div>
    </>
  );
}
